import React from "react";
import { useSelector, useStore } from "react-redux";
import { RootState } from "../../../redux/store";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import {
    AuditJob,
    AuditJobStatus,
    fetchAuditReport,
    hydrateInFlightAudits,
    setAuditCompletionEvent,
    setAuditJob,
} from "../../../redux/slice/AI/AiAuditSlice";
import {
    AppConfigInfo,
    setSingleAlertObj,
} from "../../../redux/slice/commonSlice";
import { selectIsValueFoundInToken } from "../../../redux/slice/authSlice";
import { CAN_ACCESS_APPLICATION } from "../../../constants/applicationConstants";

// Map backend status strings (UPPERCASE) to AuditJob slice values
// (lowercase). Returns null for unrecognised values so we silently drop
// rows in unexpected states rather than poisoning the auditJobs map.
const mapBackendAuditStatus = (
    backendStatus: string | null | undefined,
): AuditJobStatus | null => {
    if (!backendStatus) return null;
    switch (String(backendStatus).toUpperCase()) {
        case "UPLOADING":
            return "uploading";
        case "PROCESSING":
            return "processing";
        case "COMPLETED":
        case "SUCCESS":
        case "SUCCEEDED":
            return "completed";
        case "FAILED":
        case "ERROR":
            return "failed";
        default:
            return null;
    }
};

const DEFAULT_AUDIT_POLL_INTERVAL_MS = 5000;
const DEFAULT_AUDIT_POLL_MAX_ATTEMPTS = 40;

const readNumericConfig = (
    appConfigList: AppConfigInfo[] | undefined,
    name: string,
): number | null => {
    if (!appConfigList) return null;
    const item = appConfigList.find((c) => c?.configName === name);
    if (!item || item.configValue === null || item.configValue === undefined) return null;
    const n = Number(item.configValue);
    return Number.isFinite(n) ? n : null;
};

interface AuditJobPollerProps {
    documentId: string;
    auditId: string;
}

// One poller instance per active job. Mounting/unmounting is driven by the
// parent manager — when the parent stops listing this job (because the
// status flipped to completed/failed), the cleanup function cancels any
// pending poll timer.
const AuditJobPoller: React.FC<AuditJobPollerProps> = ({ documentId, auditId }) => {
    const dispatch = useAppDispatch();
    const appConfigList = useSelector(
        (state: RootState) => state.commonSlice.appConfigList,
    );
    // Read this job's fileMetadata so we can carry the patient context
    // through to the global completion notification.
    const jobPatientIdAtStart = useSelector(
        (state: RootState) =>
            state.aiAuditSlice.auditJobs[documentId]?.fileMetadata?.patientIdAtStart,
    );
    const jobFileName = useSelector(
        (state: RootState) =>
            state.aiAuditSlice.auditJobs[documentId]?.fileMetadata?.fileName,
    );

    React.useEffect(() => {
        let cancelled = false;
        let timeoutId: number | undefined;
        let attempt = 0;

        const intervalSec = readNumericConfig(appConfigList, "medical.note.statusPollInterval");
        const maxAttempts = readNumericConfig(appConfigList, "medical.note.maxAttempt");
        const POLL_INTERVAL_MS =
            intervalSec && intervalSec > 0 ? intervalSec * 1000 : DEFAULT_AUDIT_POLL_INTERVAL_MS;
        const MAX_ATTEMPTS =
            maxAttempts && maxAttempts > 0 ? Math.floor(maxAttempts) : DEFAULT_AUDIT_POLL_MAX_ATTEMPTS;

        const poll = async () => {
            if (cancelled) return;
            attempt += 1;

            try {
                const result = await dispatch(fetchAuditReport(auditId));
                if (cancelled) return;

                if (result.meta.requestStatus !== "fulfilled") {
                    throw new Error(
                        typeof result.payload === "string" && result.payload
                            ? result.payload
                            : "Failed to fetch audit status",
                    );
                }

                const reportPayload: any = result.payload;
                const responseJson =
                    reportPayload?.data?.responseJson || reportPayload?.responseJson || {};
                const docIdFromResponse =
                    reportPayload?.data?.documentId || reportPayload?.documentId || documentId;
                const status = String(responseJson?.status || "").toLowerCase();

                if (status === "failed") {
                    const errMsg =
                        responseJson?.error ||
                        reportPayload?.errors?.[0]?.message ||
                        reportPayload?.message ||
                        "Audit failed";
                    dispatch(setAuditJob({
                        documentId,
                        job: { status: "failed", error: errMsg },
                    }));
                    dispatch(setSingleAlertObj({ message: errMsg, alertType: "error" }));
                    return;
                }

                if (
                    status === "completed" ||
                    status === "success" ||
                    status === "succeeded"
                ) {
                    const auditData = { ...responseJson, documentId: docIdFromResponse };
                    dispatch(setAuditJob({
                        documentId,
                        job: { status: "completed", data: auditData, error: null },
                    }));

                    // Resolve patient context — prefer the values returned
                    // by the backend on this completion response, fall back
                    // to the snapshot we captured when the audit started
                    // (fileMetadata) so we always have something usable.
                    const patientIdFromResponse =
                        reportPayload?.data?.patientId ??
                        reportPayload?.patientId ??
                        null;
                    const patientNameFromResponse =
                        reportPayload?.data?.patientName ??
                        reportPayload?.patientName ??
                        "";
                    const fileNameFromResponse =
                        reportPayload?.data?.fileName ??
                        reportPayload?.fileName ??
                        "";
                    const patientNameFromAuditMetadata: string | undefined =
                        responseJson?.metadata?.patient_name;

                    const resolvedPatientId =
                        patientIdFromResponse !== null && patientIdFromResponse !== undefined
                            ? patientIdFromResponse
                            : jobPatientIdAtStart ?? null;
                    const resolvedPatientName =
                        (patientNameFromResponse && String(patientNameFromResponse).trim()) ||
                        (patientNameFromAuditMetadata && patientNameFromAuditMetadata.trim()) ||
                        "";
                    const resolvedFileName =
                        (fileNameFromResponse && String(fileNameFromResponse).trim()) ||
                        (jobFileName && jobFileName.trim()) ||
                        "";

                    // Carry the per-audit context through the slice so the
                    // global completion notification's VIEW REPORT button
                    // always opens THIS audit's report — even when several
                    // audits across multiple patients complete in parallel.
                    dispatch(setAuditCompletionEvent({
                        documentId,
                        auditId,
                        patientId: resolvedPatientId,
                        patientName: resolvedPatientName,
                        fileName: resolvedFileName,
                        auditData,
                        completedAt: Date.now(),
                    }));
                    return;
                }

                if (attempt >= MAX_ATTEMPTS) {
                    const errMsg =
                        "Audit is taking longer than expected. Please try again in a few minutes.";
                    dispatch(setAuditJob({
                        documentId,
                        job: { status: "failed", error: errMsg },
                    }));
                    dispatch(setSingleAlertObj({ message: errMsg, alertType: "error" }));
                    return;
                }

                timeoutId = window.setTimeout(poll, POLL_INTERVAL_MS);
            } catch (error: any) {
                if (cancelled) return;
                const errMsg = error?.message || "Failed to fetch audit status";
                dispatch(setAuditJob({
                    documentId,
                    job: { status: "failed", error: errMsg },
                }));
                dispatch(setSingleAlertObj({ message: errMsg, alertType: "error" }));
            }
        };

        poll();

        return () => {
            cancelled = true;
            if (timeoutId !== undefined) {
                window.clearTimeout(timeoutId);
            }
        };
    }, [auditId, documentId, dispatch, appConfigList, jobPatientIdAtStart, jobFileName]);

    return null;
};


const MedicalNoteAuditPollingManager: React.FC = () => {
    const dispatch = useAppDispatch();
    // Store handle so we can read the latest auditJobs at iteration time
    // when merging hydration rows in — protects an audit the user just
    // started locally from being overwritten by a slower hydration
    // response that hadn't seen it yet.
    const store = useStore<RootState>();
    const auditJobs = useSelector(
        (state: RootState) => state.aiAuditSlice.auditJobs,
    );

    const userId = useSelector(
        (state: RootState) => state.authSlice?.userInfo?.userId,
    );
    const canAccessMedicalAudit = useSelector((state: RootState) =>
        selectIsValueFoundInToken(
            "canAccessApplication",
            CAN_ACCESS_APPLICATION.MEDICAL_NOTE_AUDIT,
        )(state),
    );

    const hydratedForUserIdRef = React.useRef<number | null>(null);

    React.useEffect(() => {
        if (!canAccessMedicalAudit) return;
        if (!userId || userId === 0) return;
        if (hydratedForUserIdRef.current === userId) return;
        hydratedForUserIdRef.current = userId;

        (async () => {
            const result = await dispatch(hydrateInFlightAudits(userId));
            if (result.meta.requestStatus !== "fulfilled") return;
            // Endpoint returns a flat array directly under `data`. Guard
            // for empty / malformed payloads so we never iterate a non-
            // array.
            const rawData: unknown = (result.payload as any)?.data;
            const rows: any[] = Array.isArray(rawData) ? rawData : [];
            for (const row of rows) {
                if (!row?.documentId || !row?.auditId) continue;
                const mapped = mapBackendAuditStatus(row?.status);
                if (!mapped) continue;
                const docIdKey = String(row.documentId);
                // Read fresh state for each row — a job the user just
                // dispatched into the slice (optimistic uploading state
                // for the same docId) must win over the backend's
                // slightly-stale "processing" snapshot.
                if (store.getState().aiAuditSlice.auditJobs[docIdKey]) continue;
                dispatch(
                    setAuditJob({
                        documentId: docIdKey,
                        job: {
                            auditId: String(row.auditId),
                            status: mapped,
                            data: null,
                            error: null,
                            fileMetadata: {
                                fileName: row.fileName || "",
                                // Medical-note audits are always PDF.
                                fileType: "application/pdf",
                                docType: "medical notes",
                                patientIdAtStart:
                                    row.patientId !== undefined &&
                                    row.patientId !== null
                                        ? row.patientId
                                        : null,
                                patientNameAtStart:
                                    typeof row.patientName === "string" &&
                                    row.patientName.trim()
                                        ? row.patientName.trim()
                                        : undefined,
                            },
                        },
                    }),
                );
            }
        })();
    }, [userId, canAccessMedicalAudit, dispatch, store]);

    const processingJobs = React.useMemo(() => {
        return Object.values(auditJobs).filter(
            (j: AuditJob) => j.status === "processing" && !!j.auditId,
        );
    }, [auditJobs]);

    return (
        <>
            {processingJobs.map((job) => (
                <AuditJobPoller
                    key={job.documentId}
                    documentId={job.documentId}
                    auditId={job.auditId as string}
                />
            ))}
        </>
    );
};

export default MedicalNoteAuditPollingManager;
